'use client';

import { c as _c } from "react/compiler-runtime";
import { type Factiii, FactiiiRequirement, factiiiRequirementDescriptions, type PinnedFactiii } from '@shared/all/domains/factiiis';
import { getDimensionsFromUri } from '@shared/utils/helpers/uploadHelper';
import useSpace from '@shared/utils/hooks/useSpace';
import useUpload from '@shared/utils/hooks/useUpload';
import { Camera, Minus, Plus } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import { getS3ObjectURL } from '../../utils/s3';
import { client, type RouterOutputs } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import Upload from '../common/Upload';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
import Input from '../UI/Input';
enum UploadType {
  BANNER = 'Banner',
  AVATAR = 'Avatar',
}
type Props = {
  closeHandler: () => void;
  factiiiId?: number | null;
  factiiiQuery?: Factiii;
  pinnedFactiiis: PinnedFactiii[];
  space: RouterOutputs['spaces']['get'];
};
type SpaceData = {
  description: string | null;
};
const SpaceEdit = props => {
  const $ = _c(86);
  const {
    updateSpace
  } = useSpace();
  const {
    upload,
    addFile,
    files,
    clear
  } = useUpload(client);
  const t0 = props.space.description ?? null;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      description: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const initialData = t1;
  const avatarKey = files.avatar?.uri ?? props.space.avatarKey ?? null;
  const bannerKey = files.banner?.uri ?? props.space.bannerKey ?? null;
  const [spaceData, setSpaceData] = useState(initialData);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(null);
  const [isRequirementSectionOpen, setIsRequirementSectionOpen] = useState(false);
  let t2;
  if ($[2] !== props.factiiiQuery?.requirements) {
    t2 = props.factiiiQuery?.requirements as FactiiiRequirement[] || [];
    $[2] = props.factiiiQuery?.requirements;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [selectedRequirements, setSelectedRequirements] = useState(t2);
  let t3;
  if ($[4] !== files.avatar || $[5] !== files.banner || $[6] !== initialData.description || $[7] !== props.factiiiQuery?.requirements || $[8] !== selectedRequirements || $[9] !== spaceData.description) {
    t3 = spaceData.description !== initialData.description || !!files.avatar || !!files.banner || JSON.stringify(selectedRequirements) !== JSON.stringify(props.factiiiQuery?.requirements as FactiiiRequirement[] || []);
    $[4] = files.avatar;
    $[5] = files.banner;
    $[6] = initialData.description;
    $[7] = props.factiiiQuery?.requirements;
    $[8] = selectedRequirements;
    $[9] = spaceData.description;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const hasChanges = t3;
  let t4;
  if ($[11] !== files.avatar || $[12] !== files.banner || $[13] !== props || $[14] !== selectedRequirements || $[15] !== spaceData.description || $[16] !== updateSpace || $[17] !== upload) {
    t4 = async () => {
      setButtonLoading(true);
      const avatarChanged = !!files.avatar;
      const bannerChanged = !!files.banner;
      const results = await upload();
      if ((avatarChanged || bannerChanged) && Object.keys(results).length === 0) {
        setButtonLoading(false);
        return;
      }
      const avatarId = results.avatar?.id;
      const bannerId = results.banner?.id;
      const [error, updatedSpace] = await client.spaces.update.mutate({
        slug: props.space.spaceSlug,
        description: spaceData.description ?? undefined,
        factiiiId: props.factiiiId,
        bannerId: bannerChanged ? bannerId : undefined,
        avatarId: avatarChanged ? avatarId : undefined,
        requirements: selectedRequirements
      });
      if (error) {
        return setButtonLoading(false);
      }
      updateSpace({
        ...props.space,
        ...updatedSpace
      });
      props.closeHandler();
      setButtonLoading(false);
    };
    $[11] = files.avatar;
    $[12] = files.banner;
    $[13] = props;
    $[14] = selectedRequirements;
    $[15] = spaceData.description;
    $[16] = updateSpace;
    $[17] = upload;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const saveHandler = t4;
  let t5;
  if ($[19] !== addFile) {
    t5 = type => async file => {
      const key = type === UploadType.AVATAR ? "avatar" : "banner";
      const uri = URL.createObjectURL(file);
      const dimensions = await getDimensionsFromUri(uri);
      addFile(key, {
        uri,
        fileName: file.name,
        width: dimensions?.width,
        height: dimensions?.height
      });
      setUploadModalOpen(null);
    };
    $[19] = addFile;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const uploadCallbackFunction = t5;
  let t6;
  if ($[21] !== selectedRequirements) {
    t6 = requirement => {
      const isSelected = selectedRequirements.includes(requirement);
      if (isSelected) {
        setSelectedRequirements(prevSelected => prevSelected.filter(item => item !== requirement));
      } else {
        setSelectedRequirements(prevSelected_0 => [...prevSelected_0, requirement]);
      }
    };
    $[21] = selectedRequirements;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const handleCheckboxChange = t6;
  let t7;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setUploadModalOpen(UploadType.BANNER);
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Button text="Edit Cover Photo" variant="CLEAR" classNames="absolute top-2 w-auto right-2" onClick={t7} titleElement={<div className="flex items-center justify-around space-x-2 text-sm font-semibold"><Camera height={15} width={15} /><p>Edit Cover Photo</p></div>} />;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      width: "calc(100% + 1rem)",
      height: "auto"
    };
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== bannerKey) {
    t10 = bannerKey ? getS3ObjectURL(bannerKey) : "/images/header-image-default.jpg";
    $[26] = bannerKey;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== bannerKey) {
    t11 = bannerKey?.startsWith("blob:");
    $[28] = bannerKey;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const t12 = !!t11;
  let t13;
  if ($[30] !== t10 || $[31] !== t12) {
    t13 = <div className="relative">{t8}<Image height={0} width={0} sizes="100vw" style={t9} src={t10} unoptimized={t12} alt="space banner" className="rounded-lg" /></div>;
    $[30] = t10;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== avatarKey) {
    t14 = <Avatar size={100} spaceAvatarKey={avatarKey} className="-mt-14 ml-4 border-4 border-background-secondary" />;
    $[33] = avatarKey;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="absolute bottom-2 right-0"><Button variant="CLEAR" onClick={() => setUploadModalOpen(UploadType.AVATAR)} icon={<Camera height={15} width={15} />} tooltip="Edit Space Avatar" /></div>;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t14) {
    t16 = <div className="flex"><div className="relative">{t14}{t15}</div></div>;
    $[36] = t14;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t13 || $[39] !== t16) {
    t17 = <>{t13}{t16}</>;
    $[38] = t13;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  const BannerComponent = t17;
  const t18 = props.factiiiId ? "Edit Factiii" : "Edit Space";
  let t19;
  if ($[41] !== t18) {
    t19 = <h2>{t18}</h2>;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== props.closeHandler || $[44] !== t19) {
    t20 = <ModalHeader closeHandler={props.closeHandler}>{t19}</ModalHeader>;
    $[43] = props.closeHandler;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  const t21 = spaceData.description ?? "";
  let t22;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = value => setSpaceData(prev => ({
      ...prev,
      description: value || null
    }));
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== t21) {
    t23 = <Input type="textarea" label="Description" value={t21} onChange={t22} />;
    $[47] = t21;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== handleCheckboxChange || $[50] !== isRequirementSectionOpen || $[51] !== props.factiiiQuery || $[52] !== selectedRequirements) {
    t24 = props.factiiiQuery && <div className="mt-4"><div className="flex items-center justify-between"><p>Edit Requirements</p><Button titleElement={<div className="rounded-full">{isRequirementSectionOpen ? <Minus height={10} width={10} /> : <Plus height={10} width={10} />}{" "}</div>} classNames="hover:border-ds-border-standard" onClick={() => setIsRequirementSectionOpen(!isRequirementSectionOpen)} /></div>{isRequirementSectionOpen && <div className="mt-2">{Object.values(FactiiiRequirement).map(requirement_0 => <div key={requirement_0} className="mt-2 flex items-center"><input type="checkbox" id={requirement_0} value={requirement_0} checked={selectedRequirements.includes(requirement_0)} onChange={() => handleCheckboxChange(requirement_0)} className="mr-2" /><label className="flex flex-col text-textPrimary" htmlFor={requirement_0}><span className="font-emphasis">{requirement_0}</span>{selectedRequirements.includes(requirement_0) && <span className="text-sm text-textSecondary/60">{factiiiRequirementDescriptions[requirement_0]}</span>}</label></div>)}</div>}</div>;
    $[49] = handleCheckboxChange;
    $[50] = isRequirementSectionOpen;
    $[51] = props.factiiiQuery;
    $[52] = selectedRequirements;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== BannerComponent || $[55] !== t23 || $[56] !== t24) {
    t25 = <ModalContent banner={BannerComponent}>{t23}{t24}</ModalContent>;
    $[54] = BannerComponent;
    $[55] = t23;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== props.closeHandler) {
    t26 = <Button text="Close" variant="SECONDARY" onClick={props.closeHandler} />;
    $[58] = props.closeHandler;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== clear || $[61] !== hasChanges || $[62] !== initialData || $[63] !== props.factiiiQuery?.requirements) {
    t27 = hasChanges && <Button text="Cancel" variant="SECONDARY" onClick={() => {
      setSpaceData(initialData);
      clear();
      setSelectedRequirements(props.factiiiQuery?.requirements as FactiiiRequirement[] || []);
    }} />;
    $[60] = clear;
    $[61] = hasChanges;
    $[62] = initialData;
    $[63] = props.factiiiQuery?.requirements;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  const t28 = buttonLoading || !hasChanges;
  let t29;
  if ($[65] !== buttonLoading || $[66] !== saveHandler || $[67] !== t28) {
    t29 = <Button text="Save" onClick={saveHandler} loading={buttonLoading} disabled={t28} />;
    $[65] = buttonLoading;
    $[66] = saveHandler;
    $[67] = t28;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== t27 || $[70] !== t29) {
    t30 = <div className="flex gap-2">{t27}{t29}</div>;
    $[69] = t27;
    $[70] = t29;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] !== t26 || $[73] !== t30) {
    t31 = <ModalFooter><div className="flex w-full items-center justify-between">{t26}{t30}</div></ModalFooter>;
    $[72] = t26;
    $[73] = t30;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  let t32;
  if ($[75] !== props.closeHandler || $[76] !== t20 || $[77] !== t25 || $[78] !== t31) {
    t32 = <Modal closeHandler={props.closeHandler}>{t20}{t25}{t31}</Modal>;
    $[75] = props.closeHandler;
    $[76] = t20;
    $[77] = t25;
    $[78] = t31;
    $[79] = t32;
  } else {
    t32 = $[79];
  }
  let t33;
  if ($[80] !== uploadCallbackFunction || $[81] !== uploadModalOpen) {
    t33 = uploadModalOpen !== null && <Upload crop={{
      enabled: true,
      aspect: uploadModalOpen === UploadType.BANNER ? 3 : 1
    }} title={uploadModalOpen === UploadType.AVATAR ? "Upload Space Avatar" : "Upload Space Cover Photo"} closeHandler={() => setUploadModalOpen(null)} callbackFn={uploadCallbackFunction(uploadModalOpen)} enableCamera={true} />;
    $[80] = uploadCallbackFunction;
    $[81] = uploadModalOpen;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  let t34;
  if ($[83] !== t32 || $[84] !== t33) {
    t34 = <>{t32}{t33}</>;
    $[83] = t32;
    $[84] = t33;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  return t34;
};
export default SpaceEdit;